/**
 * LocalPeptideGuidesHub
 *
 * Hub page for /articles/local-peptide-guides (main hub) and
 * /articles/local-peptide-guides/:market (per-market child hub).
 *
 * Fetches all local peptide guide articles from the API, applies
 * client-side market + peptide filters, and renders a browsable grid.
 * Server-rendered text in the hero section is always crawlable.
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, FlaskConical, ArrowRight, BookOpen } from "lucide-react";
import api from "../lib/api";
import Disclaimer from "../components/Disclaimer";
import { robotsMeta } from "../lib/seo_env";

const OG_IMAGE = "https://medicalpeptides.ai/social-preview.jpg";

/** Read Puppeteer-injected cache (same pattern as CmsPage.jsx). */
function readPrerenderCache(key) {
  if (typeof window === "undefined") return null;
  const cache = window.__PRERENDER_CACHE__;
  if (!cache) return null;
  return cache[key] ?? null;
}

const ORIGIN = "https://medicalpeptides.ai";

const MARKET_META = {
  "las-vegas-nv":    { name: "Las Vegas, NV",    state: "Nevada" },
  "albuquerque-nm":  { name: "Albuquerque, NM",  state: "New Mexico" },
  "coral-gables-fl": { name: "Coral Gables, FL", state: "Florida" },
  "brookfield-wi":   { name: "Brookfield, WI",   state: "Wisconsin" },
  "omaha-ne":        { name: "Omaha, NE",         state: "Nebraska" },
};

const MARKET_SLUGS = Object.keys(MARKET_META);

const LENS_LABELS = {
  evidence_regulatory:  "Evidence & Regulatory",
  candidacy:            "Candidacy",
  baseline_labs:        "Baseline Labs",
  safety_monitoring:    "Safety & Monitoring",
  realistic_expectations:"Realistic Expectations",
  route_administration: "Route & Administration",
  lifestyle_context:    "Lifestyle Context",
  visit_planning:       "Visit Planning",
  followup_continuity:  "Follow-up & Continuity",
};

function ArticleCard({ article }) {
  const extra = article.extra || {};
  const lens  = LENS_LABELS[extra.editorial_lens] || "";
  return (
    <Link
      to={`/articles/${article.slug}`}
      className="vv-card p-5 flex flex-col gap-3 hover:shadow-md transition-shadow group"
      data-testid={`guide-card-${article.slug}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {extra.market_display ? (
          <span className="flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold text-[color:var(--vv-teal)]">
            <MapPin size={11} /> {extra.market_display}
          </span>
        ) : null}
        {lens ? (
          <span className="text-[10px] uppercase tracking-widest text-stone-400 font-medium">{lens}</span>
        ) : null}
      </div>
      <h3 className="font-display text-base leading-snug text-stone-900 group-hover:text-[color:var(--vv-teal)] transition-colors">
        {article.title}
      </h3>
      {article.subtitle ? (
        <p className="text-stone-600 text-sm leading-relaxed line-clamp-2">{article.subtitle}</p>
      ) : null}
      <div className="mt-auto flex items-center gap-1 text-xs text-[color:var(--vv-teal)] font-medium">
        Read guide <ArrowRight size={12} />
      </div>
    </Link>
  );
}

export default function LocalPeptideGuidesHub() {
  const { market: marketParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed from Puppeteer prerender cache so the initial render matches the
  // snapshot HTML — eliminates the "Loading…" flash on hydration.
  const [articles, setArticles] = useState(() => {
    const cached = readPrerenderCache("/content/article");
    if (!Array.isArray(cached)) return [];
    return cached.filter(a => a.extra?.article_type === "local_peptide_guide");
  });
  const [loading, setLoading] = useState(articles.length === 0);

  const selectedMarket  = marketParam || searchParams.get("market") || "";
  const selectedPeptide = searchParams.get("peptide") || "";

  useEffect(() => {
    // Skip fetch if already hydrated from prerender cache.
    if (articles.length > 0) return;
    api.get("/content/article")
      .then(res => {
        const guides = (res.data || []).filter(
          a => a.extra?.article_type === "local_peptide_guide"
        );
        setArticles(guides);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const peptideOptions = useMemo(() => {
    const seen = new Set();
    return articles
      .filter(a => !selectedMarket || a.extra?.location_slug === selectedMarket)
      .map(a => a.extra?.peptide_slug)
      .filter(s => s && !seen.has(s) && seen.add(s));
  }, [articles, selectedMarket]);

  const filtered = useMemo(() => {
    return articles.filter(a => {
      const mOk = !selectedMarket  || a.extra?.location_slug === selectedMarket;
      const pOk = !selectedPeptide || a.extra?.peptide_slug  === selectedPeptide;
      return mOk && pOk;
    });
  }, [articles, selectedMarket, selectedPeptide]);

  const hubTitle = marketParam
    ? `${MARKET_META[marketParam]?.name || marketParam} Peptide Education Guides`
    : "Local Peptide Education Guides";

  const hubDescription = marketParam
    ? `Educational guides to peptides available for consultation at VidaVital Medical in ${MARKET_META[marketParam]?.name || marketParam}. Each guide covers a distinct clinical topic to help patients prepare for an informed provider conversation.`
    : "Educational guides covering peptide topics by VidaVital Medical market. Each article takes a unique editorial angle — evidence status, candidacy, lab workup, safety monitoring, administration, lifestyle, visit planning, or follow-up — to give you a complete picture across the catalog.";

  const breadcrumbs = marketParam
    ? [
        { name: "Home", path: "/" },
        { name: "Articles", path: "/articles" },
        { name: "Local Peptide Guides", path: "/articles/local-peptide-guides" },
        { name: MARKET_META[marketParam]?.name || marketParam, path: null },
      ]
    : [
        { name: "Home", path: "/" },
        { name: "Articles", path: "/articles" },
        { name: "Local Peptide Guides", path: null },
      ];

  const canonicalPath = marketParam
    ? `/articles/local-peptide-guides/${marketParam}`
    : "/articles/local-peptide-guides";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": hubTitle,
      "description": hubDescription,
      "url": `${ORIGIN}${canonicalPath}`,
      "publisher": {
        "@type": "Organization",
        "name": "MedicalPeptides.ai",
        "url": ORIGIN,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs
        .filter(bc => bc.path)
        .map((bc, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": bc.name,
          "item": `${ORIGIN}${bc.path}`,
        }))
        .concat([{
          "@type": "ListItem",
          "position": breadcrumbs.filter(bc => bc.path).length + 1,
          "name": hubTitle,
          "item": `${ORIGIN}${canonicalPath}`,
        }]),
    },
  ];

  return (
    <div data-testid="local-guides-hub">
      <Helmet>
        <title>{`${hubTitle} | VidaVital Medical`}</title>
        <meta name="description" content={hubDescription} />
        <meta name="robots" content={robotsMeta()} />
        <link rel="canonical" href={`${ORIGIN}${canonicalPath}`} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Medical Peptides Powered by VidaVital Medical" />
        <meta property="og:title" content={`${hubTitle} | VidaVital Medical`} />
        <meta property="og:description" content={hubDescription} />
        <meta property="og:url" content={`${ORIGIN}${canonicalPath}`} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${hubTitle} | VidaVital Medical`} />
        <meta name="twitter:description" content={hubDescription} />
        <meta name="twitter:image" content={OG_IMAGE} />
        {jsonLd.map((s, i) => (
          <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>

      {/* Hero */}
      <section className="vv-container py-10 md:py-14">
        <nav aria-label="Breadcrumb" className="text-xs text-stone-500 mb-6">
          {breadcrumbs.map((bc, i) => (
            <span key={i}>
              {i > 0 && <span className="mx-1.5">›</span>}
              {bc.path
                ? <Link to={bc.path} className="hover:text-[color:var(--vv-teal)]">{bc.name}</Link>
                : <span className="text-stone-700 font-medium">{bc.name}</span>}
            </span>
          ))}
        </nav>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--vv-teal)]/10 text-[color:var(--vv-teal)] grid place-items-center flex-shrink-0 mt-1">
            <BookOpen size={20} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-1">
              Local Peptide Education Guides
            </div>
            <h1 className="font-display text-4xl md:text-5xl">{hubTitle}</h1>
            <p className="mt-4 text-stone-600 max-w-3xl leading-relaxed">{hubDescription}</p>
            <p className="mt-2 text-sm text-stone-500">
              All articles are educational only — not medical advice. Content is reviewed by{" "}
              <Link to="/providers/christopher-patino" className="underline hover:text-[color:var(--vv-teal)]">Christopher Patino, APRN</Link>{" "}
              and{" "}
              <Link to="/providers/yessenia-quirola" className="underline hover:text-[color:var(--vv-teal)]">Dr. Yessenia Quirola, DNP</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Market hubs strip */}
      {!marketParam && (
        <section className="vv-container pb-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Browse by clinic market</div>
          <div className="flex flex-wrap gap-2" data-testid="market-hub-list">
            {MARKET_SLUGS.map(ms => (
              <Link
                key={ms}
                to={`/articles/local-peptide-guides/${ms}`}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  ms === selectedMarket
                    ? "bg-[color:var(--vv-teal)] text-white border-[color:var(--vv-teal)]"
                    : "border-stone-300 text-stone-700 hover:border-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal)]"
                }`}
                data-testid={`market-hub-link-${ms}`}
              >
                <MapPin size={12} className="inline mr-1" />
                {MARKET_META[ms]?.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="vv-container pb-6">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Market filter (only on main hub) */}
          {!marketParam && (
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-stone-400" />
              <select
                value={selectedMarket}
                onChange={e => {
                  const p = new URLSearchParams(searchParams);
                  if (e.target.value) p.set("market", e.target.value);
                  else p.delete("market");
                  p.delete("peptide");
                  setSearchParams(p);
                }}
                className="text-sm border border-stone-300 rounded-lg px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-[color:var(--vv-teal)]"
                data-testid="filter-market"
              >
                <option value="">All markets</option>
                {MARKET_SLUGS.map(ms => (
                  <option key={ms} value={ms}>{MARKET_META[ms]?.name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Peptide filter */}
          <div className="flex items-center gap-2">
            <FlaskConical size={14} className="text-stone-400" />
            <select
              value={selectedPeptide}
              onChange={e => {
                const p = new URLSearchParams(searchParams);
                if (e.target.value) p.set("peptide", e.target.value);
                else p.delete("peptide");
                setSearchParams(p);
              }}
              className="text-sm border border-stone-300 rounded-lg px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-[color:var(--vv-teal)]"
              data-testid="filter-peptide"
            >
              <option value="">All peptides</option>
              {peptideOptions.map(ps => (
                <option key={ps} value={ps}>{ps.replace(/-/g, " ")}</option>
              ))}
            </select>
          </div>
          <span className="text-sm text-stone-500" data-testid="article-count">
            {loading ? "Loading…" : `${filtered.length} guide${filtered.length !== 1 ? "s" : ""}`}
          </span>
        </div>
      </section>

      {/* Article grid */}
      <section className="vv-container pb-16" data-testid="guides-grid">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({length: 6}).map((_, i) => (
              <div key={i} className="vv-card p-5 animate-pulse h-36" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-stone-500 py-8 text-center">No guides found for the selected filters.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(a => <ArticleCard key={a.slug} article={a} />)}
          </div>
        )}
      </section>

      {/* Market child hub nav (on market-specific page) */}
      {marketParam && (
        <section className="vv-container pb-8">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Other markets</div>
          <div className="flex flex-wrap gap-2">
            {MARKET_SLUGS.filter(ms => ms !== marketParam).map(ms => (
              <Link
                key={ms}
                to={`/articles/local-peptide-guides/${ms}`}
                className="px-4 py-2 rounded-full text-sm border border-stone-300 text-stone-700 hover:border-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal)] transition-colors"
                data-testid={`other-market-${ms}`}
              >
                {MARKET_META[ms]?.name}
              </Link>
            ))}
            <Link
              to="/articles/local-peptide-guides"
              className="px-4 py-2 rounded-full text-sm border border-stone-300 text-stone-700 hover:border-[color:var(--vv-teal)] hover:text-[color:var(--vv-teal)] transition-colors"
              data-testid="all-markets-link"
            >
              All markets
            </Link>
          </div>
        </section>
      )}

      <div className="vv-container pb-10">
        <Disclaimer />
      </div>
    </div>
  );
}
